import type { GlobalCalendarLocale, OwaDate } from 'owa-datetime';
import type { OwaDateTimeLocalizedStringResourceId } from 'owa-datetime-formatters-core';
import { getLocalizedString } from 'owa-datetime-formatters-core';
import { format } from 'owa-localize';
import { getDateFormat } from 'owa-datetime-store';
import { getDateSpecifiersOrder } from './getDateSpecifiersOrder';
import { getDaySpecifier } from './getDaySpecifier';
import { formatIntlDate } from './intlFormatters';
import formatDate from './formatDate';

/**
 * Formats a single calendar date string according to the user’s locale / settings.
 *
 * @param date         – The OwaDate instance to be formatted.
 * @param locale       – (Optional) Calendar locale
 * @param useShortMonth– If true, use abbreviated month names (e.g. “Jan” instead of “January”).
 * @param useWeekDay   – If true, prefix the formatted date with the weekday (e.g. “Mon, ”).
 * @returns A locale‑aware, fully formatted date string.
 */
export default function formatSingleDate(
    date: OwaDate,
    locale?: GlobalCalendarLocale,
    useShortMonth = false,
    useWeekDay = false
): string {
    const dateFormat = getDateFormat();
    const order = getDateSpecifiersOrder(dateFormat); // “dMy” | “Mdy” | “ydM” | “yMd”
    const daySpecifier = getDaySpecifier(dateFormat); // “d”/“dd” per user setting

    const resourceIdMap: Record<string, OwaDateTimeLocalizedStringResourceId> = useShortMonth
        ? {
              dMy: 'singleDateShortMonthFormat_dMy',
              Mdy: 'singleDateShortMonthFormat_Mdy',
              ydM: 'singleDateShortMonthFormat_ydM',
              yMd: 'singleDateShortMonthFormat_yMd',
          }
        : {
              dMy: 'singleDateFormat_dMy',
              Mdy: 'singleDateFormat_Mdy',
              ydM: 'singleDateFormat_ydM',
              yMd: 'singleDateFormat_yMd',
          };

    const basePattern = getLocalizedString(resourceIdMap[order]);
    const baseFormatted = format(basePattern, daySpecifier);

    const finalPattern = useWeekDay
        ? format(getLocalizedString('singleDateUseWeekDayFormat'), baseFormatted)
        : baseFormatted;

    return locale ? formatIntlDate(date, locale, finalPattern) : formatDate(date, finalPattern);
}
